/**
 * Records Tab - Embedded dashboard records view
 *
 * Uses embedded-specific hooks (useEmbeddedApiClient, useEmbeddedOrg) and the
 * portal's list core `useRecordsList` with them — `useRecordsData` itself reads
 * the Clerk auth context, which /embedded/* does not have.
 *
 * Compact master-detail layout optimized for Bitrix24 iframe.
 */
import { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ChevronLeft,
  Filter,
  X,
  RefreshCw,
  Radio,
  Mic,
  Brain,
  Send,
  AlertTriangle,
  FileAudio,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { recordChannelIcon } from '@/lib/records/channel-icon';
import { callCategoryLabelKey } from '@/lib/records/call-category-label';
import { RecordDetailInline } from '@/components/records/RecordDetailInline';
import { useAccessChange, useStaleAfterAccessChange } from '@/hooks/useAccessChange';
import { buildTimestampsTooltip, formatNumber, formatRelativeTime } from '@/lib/utils';
import { useRecordsFilters, useRecordsList } from '@/hooks/records';
import { formatRecordsTotal } from '@/hooks/records/records-paging';
import type { DirectionFilter } from '@/hooks/records/useRecordsFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { SearchDegradedNotice } from '@/components/filters/SearchDegradedNotice';
import { SearchDeeperNotice } from '@/components/filters/SearchDeeperNotice';
import { SearchCappedNotice } from '@/components/filters/SearchCappedNotice';
import { SearchTruncatedNotice } from '@/components/filters/SearchTruncatedNotice';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { useEmbeddedApiClient } from '@/hooks/useEmbeddedApiClient';
import { useEmbeddedProcessingState } from '@/features/processing/useEmbeddedProcessingState';
import { useQuotedStart } from '@/features/processing/useQuotedStart';
import { StartQuoteConfirmDialog } from '@/features/processing/StartQuoteConfirmDialog';
import { isFailedRecordStatus } from '@/lib/records/status';
import { getCategoryTagStyle } from '@/lib/categoryBadgeStyle';
import { CrmEntityLink } from '@/components/data/CrmEntityLink';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/ui/skeletons';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { RECORD_SEARCH_MAX_LENGTH, type CallCategory } from '@aica/api-types';
import { formatQualityScore } from '@/lib/format-quality-score';
import { CONTROL_MIN_HEIGHT } from '@/components/ui/control-size';
import { RECORD_REPORT_HEIGHT_PX } from '@/lib/embedded-content-sized';
import { useScrollParentTo } from '@/hooks/useScrollParentTo';
import { embeddedBitrix24Domain } from '@/lib/embedded-host';

const QA_ICON_PULSING = 'h-3.5 w-3.5 animate-pulse text-info inline-block';
const QA_ICON_STILL = 'h-3.5 w-3.5 text-info inline-block';
const QA_ICON_ERROR = 'h-3.5 w-3.5 text-danger inline-block';

const PROCESSING_ICON_BY_STATUS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  pending: Radio,
  awaiting_media: FileAudio,
  awaiting_chat_completion: FileAudio,
  transcribing: Mic,
  transcribed: Mic,
  analyzing: Brain,
  analyzed: Brain,
  delivering: Send,
};

const STILL_PROCESSING_STATUSES = new Set(['transcribed', 'analyzed']);

const PAGE_SIZE = 20;

interface RecordsTabProps {
  initialRecordId?: string;
  segmentStartSeconds?: number;
  segmentDurationSeconds?: number;
  alertId?: string;
  /** Client quote to mark in the initial record's transcript (#846). */
  highlightQuote?: string;
  onClearDeepLink?: () => void;
}

export function RecordsTab({
  initialRecordId,
  segmentStartSeconds,
  segmentDurationSeconds,
  alertId,
  highlightQuote,
  onClearDeepLink,
}: RecordsTabProps) {
  const { t } = useTranslation(['records', 'common', 'dashboard']);
  const [localSelectedRecordId, setLocalSelectedRecordId] = useState<string | null>(null);
  const chosenRecordId = initialRecordId ?? localSelectedRecordId;
  // The viewer's access changed (E1.5 PR 4b): the open record closes — the one
  // picked from the list and the one a deep link opened — in the very render
  // the change lands in, before the inline detail asks for it again; the effect
  // makes it stick. Opening it again asks the API afresh (A11).
  const closedByAccess = useStaleAfterAccessChange(chosenRecordId);
  useAccessChange(() => {
    setLocalSelectedRecordId(null);
    onClearDeepLink?.();
  });
  const selectedRecordId = closedByAccess ? null : chosenRecordId;

  // The frame is as tall as its content and scrolls in the Bitrix24 page, where
  // this document cannot read the position (the SDK only sets it). Opening a
  // record swaps a long list for a report of its own height and the page is
  // clamped to the end of the new, shorter document: the portal page is scrolled
  // to the report, and «Back» scrolls it to the row it was opened from (#2679).
  const scrollParentTo = useScrollParentTo();
  const detailRef = useRef<HTMLDivElement>(null);
  const openedFromId = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (selectedRecordId) {
      if (detailRef.current) scrollParentTo(detailRef.current);
      return;
    }
    const id = openedFromId.current;
    openedFromId.current = null;
    if (!id) return;
    const row = document.querySelector(`[data-record-id="${CSS.escape(id)}"]`);
    if (row) scrollParentTo(row);
  }, [selectedRecordId, scrollParentTo]);
  const [showFilters, setShowFilters] = useState(false);

  // Embedded-specific hooks (no Clerk dependency)
  const { orgId: selectedOrgId, role, domain, crmVendor } = useEmbeddedOrg();
  const bitrix24Domain = embeddedBitrix24Domain(crmVendor, domain);
  const { apiClient } = useEmbeddedApiClient();
  // Право/обработчик запуска для карточки открытой записи (RecordHaltedNotice
  // внутри RecordDetailInline) — свой хук, не портальный useProcessingState:
  // тот читает UnifiedAuthContext (Clerk), которого под /embedded/* нет.
  const processing = useEmbeddedProcessingState();
  // «Проанализировать» в карточке — с ценой до списания, тем же путём, что
  // в веб-версии (#1389).
  const singleStart = useQuotedStart({
    quoteStart: processing.quoteStart,
    startRecords: processing.startRecords,
    orgId: selectedOrgId,
    // Организация входит в область: та же открытая запись после смены
    // организации — уже другой контекст цены.
    scopeKey: selectedRecordId ? `${selectedOrgId}:${selectedRecordId}` : null,
  });

  // Reuse the same filter state as main app
  const {
    searchQuery,
    setSearchQuery,
    channelFilter,
    setChannelFilter,
    directionFilter,
    setDirectionFilter,
    categoryFilter,
    setCategoryFilter,
    hideMissedCalls,
    toggleHideMissedCalls,
    filterRecords,
  } = useRecordsFilters();

  // Текст ищет сервер, как в веб-приложении (#1067): раньше поле меняло только
  // локальный фильтр, который текст не сверяет, и список не менялся вовсе.
  // Не длиннее предела сервера: длинный запрос дал бы ошибку, а ошибка
  // скрывает поле поиска (Codex P2, #1151).
  const debouncedSearch = useDebounce(
    searchQuery.trim().slice(0, RECORD_SEARCH_MAX_LENGTH),
    400
  );

  // Server-side filters
  const serverFilters = useMemo(
    () => ({
      type: channelFilter !== 'all' ? channelFilter : undefined,
      callCategory: categoryFilter !== 'all' ? categoryFilter : undefined,
      // Направление тоже решает сервер: локальный фильтр его не сверяет, и
      // выбор в панели ничего не менял (ревью #1151).
      callDirection: directionFilter !== 'all' ? directionFilter : undefined,
      excludeMissed: hideMissedCalls && categoryFilter !== 'missed' ? true : undefined,
      search: debouncedSearch || undefined,
    }),
    [channelFilter, categoryFilter, directionFilter, hideMissedCalls, debouncedSearch]
  );

  // Тот же список порциями, что в веб-приложении (#1067, ревью #1151): курсор
  // поиска, конец по несдвинувшемуся курсору, автодогрузка коротких страниц и
  // пометки — одни правила (`records-paging.ts`). Вкладка показывает
  // накопленное и догружает кнопкой.
  const list = useRecordsList({
    apiClient,
    selectedOrgId,
    itemsPerPage: PAGE_SIZE,
    serverFilters,
  });
  const records = list.records;
  const loading = list.loading;
  const refresh = () => void list.refetchRecords();

  // Client-side filtering (participant, activity)
  const filteredRecords = useMemo(
    () => filterRecords(records, []),
    [filterRecords, records]
  );

  const totalLabel = formatRecordsTotal({
    total: list.totalRecords,
    partial: list.searchTotalIsPartial,
  });

  // If a record is selected, show detail view inline (within the tab panel).
  // A fixed height + flex-col gives the internal flex-1/min-h-0/overflow-hidden
  // chain inside RecordDetailInline a real height to anchor on, so transcript
  // and analysis grid rows don't collapse.
  if (selectedRecordId) {
    return (
      <div
        ref={detailRef}
        className="flex flex-col"
        style={{ height: RECORD_REPORT_HEIGHT_PX }}
        data-testid="records-detail-frame"
      >
        <button
          type="button"
          onClick={() => {
            if (initialRecordId) {
              onClearDeepLink?.();
            } else {
              setLocalSelectedRecordId(null);
            }
          }}
          className={cn(
            CONTROL_MIN_HEIGHT.sm,
            'shrink-0 flex items-center gap-1 text-sm text-primary hover:text-primary mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded transition-colors px-2 self-start'
          )}
        >
          <ChevronLeft className="h-4 w-4" />
          {t('common:common.back', 'Back to list')}
        </button>
        <div className="flex-1 min-h-0">
          <RecordDetailInline
            recordId={selectedRecordId}
            apiClient={apiClient}
            selectedOrgId={selectedOrgId}
            bitrix24Domain={bitrix24Domain}
            userRole={role}
            segmentStartSeconds={
              selectedRecordId === initialRecordId ? segmentStartSeconds : undefined
            }
            segmentDurationSeconds={
              selectedRecordId === initialRecordId ? segmentDurationSeconds : undefined
            }
            alertId={selectedRecordId === initialRecordId ? alertId : undefined}
            highlightQuote={
              selectedRecordId === initialRecordId ? highlightQuote : undefined
            }
            canStart={processing.canStart}
            onStart={() => void singleStart.requestStart([selectedRecordId])}
            isStarting={processing.isStarting || singleStart.isQuoting}
          />
        </div>
        <StartQuoteConfirmDialog
          dialog={singleStart.dialog}
          kind="single"
          onClose={singleStart.close}
          onConfirm={singleStart.confirm}
          confirmButtonTestId="records-single-start-confirm"
        />
      </div>
    );
  }

  // Панель фильтров — во всех состояниях: и при загрузке, и при ошибке. Иначе
  // ввод запроса сбрасывал бы фокус на скелетон, а ошибка оставляла бы одну
  // кнопку «Повторить» без поля поиска (ревью #1151).
  const toolbar = (
    <RecordsToolbar
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      channelFilter={channelFilter}
      onChannelChange={setChannelFilter}
      directionFilter={directionFilter}
      onDirectionChange={setDirectionFilter}
      categoryFilter={categoryFilter}
      onCategoryChange={setCategoryFilter}
      hideMissedCalls={hideMissedCalls}
      onToggleMissed={toggleHideMissedCalls}
      showFilters={showFilters}
      onToggleFilters={() => setShowFilters(!showFilters)}
      onRefresh={refresh}
      isRefreshing={list.isRefreshing}
    />
  );
  // Пометки поиска — те же, что над выдачей веб-приложения.
  const notices = (
    <>
      <SearchTruncatedNotice truncatedTo={list.searchTruncatedTo} className="mb-3" />
      {list.searchTotalIsPartial && (
        <SearchCappedNotice partial total={list.totalRecords} className="mb-3" />
      )}
      <SearchDegradedNotice degraded={list.searchDegraded} className="mb-3" />
    </>
  );

  // Loading state
  if (loading && records.length === 0) {
    return (
      <div>
        {toolbar}
        <TableSkeleton rows={5} />
      </div>
    );
  }

  // Error state
  if (list.error && records.length === 0) {
    return (
      <div>
        {toolbar}
        <div className="text-center py-12">
          <p className="text-danger text-sm">{list.error}</p>
          <button
            onClick={refresh}
            className={cn(
              CONTROL_MIN_HEIGHT.sm,
              'mt-2 text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded'
            )}
          >
            {t('common:common.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  const hasActiveFilters =
    searchQuery ||
    channelFilter !== 'all' ||
    directionFilter !== 'all' ||
    categoryFilter !== 'all';

  // Empty state
  if (filteredRecords.length === 0) {
    return (
      <div>
        {toolbar}
        {notices}
        {list.searchDeeper ? (
          // Пустой список, пока совпадения не кончились, — не «ничего не
          // найдено»: их порции отсеяны правами (#1048).
          <SearchDeeperNotice
            state={list.searchDeeper}
            onContinue={list.fetchNextPage}
            className="justify-center py-12"
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-fg-muted">
              {hasActiveFilters
                ? t('records:records.noMatchingRecords', 'No matching records')
                : t('dashboard:embedded.records_empty', 'No records yet')}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {toolbar}
      {notices}

      {/* Records table — compact view for embedded iframe. Fills the full frame
          width (w-full table); overflow-x-auto keeps it scrollable when the
          iframe is narrower than the content. Text columns (Activity/Agent/
          Client) stay readable via per-cell max-w + truncate. */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg-surface text-left">
              <th className="px-2 py-2 text-xs font-medium text-fg-muted w-8" />
              <th className="px-2 py-2 text-xs font-medium text-fg-muted w-10 text-center">
                {t('records:records.score', 'QA')}
              </th>
              <th className="px-2 py-2 text-xs font-medium text-fg-muted">
                {t('records:records.activity', 'Activity')}
              </th>
              <th className="px-2 py-2 text-xs font-medium text-fg-muted">
                {t('records:records.agent', 'Agent')}
              </th>
              <th className="px-2 py-2 text-xs font-medium text-fg-muted">
                {t('records:records.client', 'Client')}
              </th>
              <th className="px-2 py-2 text-xs font-medium text-fg-muted w-20">
                {t('records:records.callCategory.label', 'Category')}
              </th>
              <th className="px-2 py-2 text-xs font-medium text-fg-muted w-24">CRM</th>
              <th
                className="px-2 py-2 text-xs font-medium text-fg-muted w-16 text-center"
                title={t('records:records.agentRating', 'Agent Rating')}
              >
                {'★'}
              </th>
              <th className="px-2 py-2 text-xs font-medium text-fg-muted w-14">
                {t('records:records.duration', 'Dur.')}
              </th>
              <th className="px-2 py-2 text-xs font-medium text-fg-muted w-12 text-center">
                #
              </th>
              {/* w-24, не w-20: заголовок вырос с «Даты» до «Времени звонка»,
                  а в испанском это «Hora de la llamada» — в 80px он переносился
                  и растил высоту всей шапки. */}
              <th className="px-2 py-2 text-xs font-medium text-fg-muted w-24">
                {t('records:records.columns.conversationTime', 'Conversation time')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((record) => {
              const isFailed = isFailedRecordStatus(record.status);
              const ProcessingIcon = PROCESSING_ICON_BY_STATUS[record.status];

              const scoreColor =
                record.qualityScore == null
                  ? 'text-fg-muted'
                  : record.qualityScore < 50
                    ? 'text-danger'
                    : record.qualityScore < 70
                      ? 'text-warning'
                      : 'text-success';

              // Type icon: chat or call with direction
              const TypeIcon = recordChannelIcon(record);
              const typeColor =
                record.channel === 'chat'
                  ? 'text-info'
                  : record.callDirection === 'inbound'
                    ? 'text-success'
                    : record.callDirection === 'outbound'
                      ? 'text-primary'
                      : 'text-fg-muted';

              return (
                <tr
                  key={record.id}
                  data-record-id={record.id}
                  onClick={() => {
                    openedFromId.current = record.id;
                    setLocalSelectedRecordId(record.id);
                  }}
                  className="border-b border-border hover:bg-bg-surface cursor-pointer transition-colors"
                >
                  {/* Type icon */}
                  <td className="px-2 py-1.5 text-center">
                    <TypeIcon className={cn('h-3.5 w-3.5 inline-block', typeColor)} />
                  </td>
                  {/* Score / processing stage / failed */}
                  <td className="px-2 py-1.5 text-center">
                    {isFailed ? (
                      <AlertTriangle
                        className={QA_ICON_ERROR}
                        aria-label={record.status.replace(/_/g, ' ')}
                      />
                    ) : ProcessingIcon ? (
                      <ProcessingIcon
                        className={
                          STILL_PROCESSING_STATUSES.has(record.status)
                            ? QA_ICON_STILL
                            : QA_ICON_PULSING
                        }
                      />
                    ) : (
                      <span className={cn('font-mono font-semibold text-xs', scoreColor)}>
                        {record.qualityScore != null
                          ? formatQualityScore(record.qualityScore)
                          : '—'}
                      </span>
                    )}
                  </td>
                  {/* Activity name */}
                  <td className="px-2 py-1.5 text-fg truncate max-w-[140px] text-xs">
                    {record.crmActivityName ?? record.title ?? '—'}
                  </td>
                  {/* Agent */}
                  <td className="px-2 py-1.5 text-fg truncate max-w-[100px] text-xs">
                    {record.agentName ?? '—'}
                  </td>
                  {/* Client — two-line: name + phone mono */}
                  <td className="px-2 py-1.5 truncate max-w-[120px]">
                    <span className="text-xs text-fg block truncate">
                      {record.participantName ?? record.participantPhone ?? '—'}
                    </span>
                    {record.participantName && record.participantPhone && (
                      <span className="text-micro font-mono text-fg-muted block truncate">
                        {record.participantPhone}
                      </span>
                    )}
                  </td>
                  {/* Category badge */}
                  <td className="px-2 py-1.5">
                    {record.callCategory ? (
                      <span
                        className={cn(
                          'inline-flex items-center rounded px-1.5 py-0.5 text-micro font-medium truncate max-w-full',
                          getCategoryTagStyle(record.callCategory)
                        )}
                      >
                        {t(
                          `records:records.${callCategoryLabelKey(record.callCategory, record.channel, record.callDirection)}`,
                          record.callCategory
                        )}
                      </span>
                    ) : (
                      <span className="text-fg-muted text-micro">—</span>
                    )}
                  </td>
                  {/* CRM entity link — stop propagation so row click doesn't intercept */}
                  <td
                    className="px-2 py-1.5 truncate max-w-[96px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {record.crmEntityType && record.crmEntityId ? (
                      <CrmEntityLink
                        entityType={record.crmEntityType}
                        entityId={record.crmEntityId}
                        entityName={record.crmEntityName}
                        vendor={record.crmVendor}
                        domain={record.crmDomain}
                        showIcon={false}
                        className="text-xs text-primary"
                      />
                    ) : (
                      <span className="text-fg-muted text-xs">—</span>
                    )}
                  </td>
                  {/* Agent rating — 5 stars */}
                  <td className="px-2 py-1.5 text-center">
                    {record.agentRating != null ? (
                      <span
                        className="inline-flex gap-px leading-none"
                        title={record.agentRatingUserName ?? undefined}
                      >
                        {[1, 2, 3, 4, 5].map((i) => (
                          <span
                            key={i}
                            className={cn(
                              'text-[10px]',
                              i <= (record.agentRating ?? 0)
                                ? 'text-warning'
                                : 'text-fg-muted'
                            )}
                          >
                            {'★'}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-fg-muted text-xs">—</span>
                    )}
                  </td>
                  {/* Duration */}
                  <td className="px-2 py-1.5 text-fg-muted tabular-nums text-xs">
                    {record.duration ?? '—'}
                  </td>
                  {/* Series */}
                  <td className="px-2 py-1.5 text-center">
                    {record.callSeriesNumber ? (
                      <span className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-micro font-medium bg-primary-dim text-primary-fg">
                        #{record.callSeriesNumber}
                        {record.callSeriesTotal && `/${record.callSeriesTotal}`}
                      </span>
                    ) : null}
                  </td>
                  {/* Время разговора. Относительное («28 минут назад») — от
                      START_TIME источника, а не от момента приёма: подсказка
                      называет обе отметки, потому что расхождение между ними
                      принадлежит порталу, а не нашему анализу (#359). */}
                  <td
                    className="px-2 py-1.5 text-fg-muted text-xs"
                    title={buildTimestampsTooltip(record.eventTime, record.createdAt, {
                      call: t('records:records.timestamps.call', 'Call'),
                      received: t('records:records.timestamps.received', 'Received'),
                    })}
                  >
                    {formatRelativeTime(record.eventTime ?? record.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Накопленное и продолжение: кнопкой, или «Искать дальше», когда
          автодогрузка коротких страниц поиска дошла до предела (#1048). */}
      <div className="flex items-center justify-between gap-2 mt-4">
        <span className="text-fg-muted text-xs">
          {t(
            'dashboard:embedded.records_showing',
            'Showing {{from}}-{{to}} of {{total}}',
            {
              from: 1,
              // Grouped like the total beside it: «1-1,000 of 1,500+».
              to: formatNumber(records.length),
              total: totalLabel,
            }
          )}
        </span>
        {list.searchDeeper ? (
          <SearchDeeperNotice state={list.searchDeeper} onContinue={list.fetchNextPage} />
        ) : (
          list.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              onClick={list.fetchNextPage}
              disabled={list.isFetchingNextPage}
            >
              {t('common:common.loadMore', 'Load more')}
            </Button>
          )
        )}
      </div>
      {list.error && (
        <p role="alert" className="text-danger text-xs mt-2">
          {list.error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar with search + filters
// ---------------------------------------------------------------------------

interface RecordsToolbarProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  channelFilter: 'all' | 'call' | 'chat';
  onChannelChange: (f: 'all' | 'call' | 'chat') => void;
  directionFilter: DirectionFilter;
  onDirectionChange: (f: DirectionFilter) => void;
  categoryFilter: 'all' | CallCategory;
  onCategoryChange: (f: 'all' | CallCategory) => void;
  hideMissedCalls: boolean;
  onToggleMissed: () => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

function RecordsToolbar({
  searchQuery,
  onSearchChange,
  channelFilter,
  onChannelChange,
  directionFilter,
  onDirectionChange,
  categoryFilter,
  onCategoryChange,
  hideMissedCalls,
  onToggleMissed,
  showFilters,
  onToggleFilters,
  onRefresh,
  isRefreshing,
}: RecordsToolbarProps) {
  const { t } = useTranslation(['records', 'common']);

  return (
    <div className="mb-3 space-y-2">
      {/* Search + filter toggle */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted" />
          <Input
            value={searchQuery}
            maxLength={RECORD_SEARCH_MAX_LENGTH}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('records:records.searchPlaceholder', 'Search records...')}
            className="pl-9"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="shrink-0"
          title={t('common:common.refresh', 'Refresh')}
        >
          <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
        </Button>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="sm"
          onClick={onToggleFilters}
          className="shrink-0"
        >
          <Filter className="h-4 w-4" />
        </Button>
      </div>

      {/* Expandable filters */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Select
            value={channelFilter}
            onValueChange={(v) => onChannelChange(v as 'all' | 'call' | 'chat')}
            options={[
              {
                value: 'all',
                label: t('records:records.allChannels', 'All channels'),
              },
              { value: 'call', label: t('records:records.calls', 'Calls') },
              { value: 'chat', label: t('records:records.chats', 'Chats') },
            ]}
            size="sm"
          />
          <Select
            value={directionFilter}
            onValueChange={(v) => onDirectionChange(v as DirectionFilter)}
            options={[
              {
                value: 'all',
                label: t('records:records.allDirections', 'All directions'),
              },
              {
                value: 'inbound',
                label: t('records:records.inbound', 'Inbound'),
              },
              {
                value: 'outbound',
                label: t('records:records.outbound', 'Outbound'),
              },
              {
                value: 'unknown',
                label: t('records:records.directionUnknown', 'Direction unknown'),
              },
            ]}
            size="sm"
          />
          <Select
            value={categoryFilter}
            onValueChange={(v) => onCategoryChange(v as 'all' | CallCategory)}
            options={[
              {
                value: 'all',
                label: t('records:records.allCategories', 'All categories'),
              },
              {
                value: 'product_inquiry',
                label: t('records:records.category.product_inquiry', 'Product inquiry'),
              },
              {
                value: 'support',
                label: t('records:records.category.support', 'Support'),
              },
              {
                value: 'scheduling',
                label: t('records:records.category.scheduling', 'Scheduling'),
              },
              {
                value: 'missed',
                label: t('records:records.category.missed', 'Missed'),
              },
            ]}
            size="sm"
          />
          <label
            className={cn(
              'flex items-center gap-1.5 text-xs cursor-pointer select-none rounded px-2 py-1 transition-colors',
              hideMissedCalls
                ? 'bg-primary-dim text-primary-fg'
                : 'text-fg-muted hover:text-fg'
            )}
          >
            <input
              type="checkbox"
              checked={hideMissedCalls}
              onChange={onToggleMissed}
              className="sr-only"
            />
            <span>{t('records:records.hideMissed', 'Hide missed')}</span>
          </label>
        </div>
      )}
    </div>
  );
}
